import type {
  AcademicService,
  Authorisation,
  ReferenceRequest,
  ServiceProvider,
  ServiceRequest,
} from '../types/services'

/**
 * Institutions carry their own catalogue. Nothing about a particular university
 * is hard-coded into the module — adding one means adding a catalogue.
 */
export const serviceInstitutions = [
  { id: 'unilag', name: 'University of Lagos', short: 'UNILAG', campus: 'Akoka', pilot: false },
  {
    id: 'futa',
    name: 'Federal University of Technology, Akure',
    short: 'FUTA',
    campus: 'Main campus',
    pilot: true,
  },
]

export const academicServices: AcademicService[] = [
  /* ---------------------------------------------------------------- FUTA ---- */
  {
    id: 'svc-futa-transcript',
    institutionId: 'futa',
    category: 'Transcripts & Records',
    name: 'Academic transcript',
    description:
      'An official transcript issued by the Registry, sealed and sent to the institution or body you name.',
    eligibility: 'Current students and graduates with no outstanding institutional debt.',
    origin: 'official',
    modes: ['official_digital', 'assisted'],
    officialProvider: 'FUTA Registry — Exams and Records',
    officialMethod: 'FUTA E-Transcript Portal',
    officialUrl: 'https://futa.edu.ng',
    fees: [
      { label: 'Official transcript fee (local)', amount: 10000, payee: 'institution' },
      { label: 'Assistance fee (if you use a provider)', amount: 7500, payee: 'provider' },
    ],
    processingEstimate: '10 – 21 working days after the Registry accepts the request',
    requirements: [
      { id: 'r1', label: 'Matriculation number', source: 'institution' },
      { id: 'r2', label: 'Year of graduation or current level', source: 'institution' },
      { id: 'r3', label: 'Recipient name and postal address', source: 'institution' },
      { id: 'r4', label: 'Payment reference from the official portal', source: 'institution' },
      {
        id: 'r5',
        label: 'Signed authorisation, if someone collects on your behalf',
        source: 'campuzon',
        detail: 'Only needed for the assisted route.',
        optional: true,
      },
    ],
    requiresAuthorisation: true,
    collectionMethods: ['Registry counter', 'Authorised representative', 'Sealed courier'],
    deliveryOptions: ['Courier within Nigeria', 'International courier', 'Collect in person'],
    restrictions: [
      'The Registry will not release a transcript to a third party without written authorisation.',
      'Campuzon cannot alter, reprint or certify a transcript. Only the Registry issues it.',
    ],
    catalogueVerified: true,
    lastUpdated: '2 Aug 2026',
    sourceAuthority: 'FUTA Registry service schedule',
    popular: true,
  },
  {
    id: 'svc-futa-verification',
    institutionId: 'futa',
    category: 'Certificates & Verification',
    name: 'Certificate verification',
    description:
      'Confirmation to an employer or institution that a certificate you hold was genuinely issued by FUTA.',
    eligibility: 'Graduates. The requesting body must be named.',
    origin: 'official',
    modes: ['official_digital', 'assisted'],
    officialProvider: 'FUTA Registry — Certificate Verification Unit',
    officialMethod: 'Written application to the Registry, with a scanned certificate',
    fees: [
      { label: 'Official verification fee', amount: 15000, payee: 'institution' },
      { label: 'Assistance fee', amount: 6000, payee: 'provider' },
    ],
    processingEstimate: '5 – 14 working days',
    requirements: [
      { id: 'r1', label: 'Scanned copy of the certificate', source: 'institution' },
      { id: 'r2', label: 'Matriculation number', source: 'institution' },
      { id: 'r3', label: 'Name and address of the verifying body', source: 'institution' },
    ],
    requiresAuthorisation: false,
    collectionMethods: ['Sent directly to the verifying body'],
    deliveryOptions: ['Registry letter to the verifying body'],
    restrictions: ['Campuzon does not verify certificates itself and never issues a verification.'],
    catalogueVerified: true,
    lastUpdated: '2 Aug 2026',
    sourceAuthority: 'FUTA Registry service schedule',
    popular: true,
  },
  {
    id: 'svc-futa-english',
    institutionId: 'futa',
    category: 'Letters & Certifications',
    name: 'English proficiency letter',
    description:
      'A letter confirming your programme was taught and examined in English, accepted by many universities in place of IELTS.',
    eligibility: 'Graduates and final-year students of English-medium programmes.',
    origin: 'official',
    modes: ['official_digital'],
    officialProvider: 'FUTA Registry — Academic Affairs',
    officialMethod: 'Written application to the Registrar, addressed to the receiving university',
    fees: [{ label: 'Official fee', amount: 0, payee: 'institution', unknown: true }],
    processingEstimate: null,
    requirements: [
      { id: 'r1', label: 'Name of the receiving university', source: 'institution' },
      { id: 'r2', label: 'Programme and years of study', source: 'institution' },
      { id: 'r3', label: 'Copy of your statement of result', source: 'institution' },
    ],
    requiresAuthorisation: false,
    collectionMethods: ['Registry counter'],
    deliveryOptions: ['Collect in person', 'Email to the receiving university'],
    restrictions: [
      'The wording of the letter is entirely at the Registrar’s discretion.',
      'Campuzon cannot draft or issue this letter on the institution’s behalf.',
    ],
    catalogueVerified: false,
    lastUpdated: '2 Aug 2026',
    sourceAuthority: null,
    popular: true,
  },
  {
    id: 'svc-futa-collection',
    institutionId: 'futa',
    category: 'Collection & Delivery',
    name: 'Document collection on campus',
    description:
      'A verified representative collects a document the Registry has already released, and hands it to you or a courier.',
    eligibility: 'The document must already be marked ready by the institution.',
    origin: 'third_party',
    modes: ['assisted'],
    officialProvider: null,
    officialMethod: null,
    fees: [
      { label: 'Collection fee', amount: 5000, payee: 'provider' },
      { label: 'Courier, if you need delivery', amount: 3500, payee: 'courier' },
    ],
    processingEstimate: '1 – 3 working days once the document is ready',
    requirements: [
      { id: 'r1', label: 'Signed authorisation naming the representative', source: 'campuzon' },
      { id: 'r2', label: 'Photo of your student ID or certificate', source: 'provider' },
      { id: 'r3', label: 'Delivery address, if couriered', source: 'provider', optional: true },
    ],
    requiresAuthorisation: true,
    collectionMethods: ['Authorised representative'],
    deliveryOptions: ['Courier within Nigeria', 'Hand to you in Akure'],
    restrictions: [
      'This is an independent service. The representative has no authority to act for the university.',
    ],
    catalogueVerified: true,
    lastUpdated: '9 Aug 2026',
    sourceAuthority: 'Campuzon provider terms',
    popular: true,
  },
  {
    id: 'svc-futa-reference',
    institutionId: 'futa',
    category: 'References & Recommendations',
    name: 'Reference or recommendation letter',
    description:
      'Ask a lecturer or supervisor for a reference. They write and submit it themselves — Campuzon only carries the request.',
    eligibility: 'Anyone with a referee willing to act.',
    origin: 'assisted',
    modes: ['assisted'],
    officialProvider: null,
    officialMethod: null,
    fees: [{ label: 'No fee', amount: 0, payee: 'campuzon' }],
    processingEstimate: 'Depends entirely on your referee',
    requirements: [
      { id: 'r1', label: 'Referee name, role and email address', source: 'campuzon' },
      { id: 'r2', label: 'Who the reference is for, and by when', source: 'campuzon' },
      { id: 'r3', label: 'Your CV, so the referee has something to work from', source: 'campuzon' },
    ],
    requiresAuthorisation: false,
    collectionMethods: ['Submitted by the referee'],
    deliveryOptions: ['Direct to recipient', 'Returned to you', 'Campuzon verified link'],
    restrictions: [
      'Campuzon never writes, edits or signs a reference.',
      'A confidential reference is not readable by you at any point.',
    ],
    catalogueVerified: true,
    lastUpdated: '9 Aug 2026',
    sourceAuthority: 'Campuzon service terms',
    popular: true,
  },
  {
    id: 'svc-futa-statement',
    institutionId: 'futa',
    category: 'Transcripts & Records',
    name: 'Statement of result',
    description: 'An interim record of results for use before your certificate is issued.',
    eligibility: 'Graduates awaiting certificate collection.',
    origin: 'official',
    modes: ['official_digital', 'assisted'],
    officialProvider: 'FUTA Registry — Exams and Records',
    officialMethod: 'Application at the Exams and Records office',
    fees: [{ label: 'Official fee', amount: 5000, payee: 'institution' }],
    processingEstimate: '5 – 10 working days',
    requirements: [
      { id: 'r1', label: 'Matriculation number', source: 'institution' },
      { id: 'r2', label: 'Clearance from your department', source: 'institution' },
    ],
    requiresAuthorisation: true,
    collectionMethods: ['Registry counter', 'Authorised representative'],
    deliveryOptions: ['Collect in person', 'Courier within Nigeria'],
    restrictions: ['Superseded by the certificate once issued.'],
    catalogueVerified: true,
    lastUpdated: '2 Aug 2026',
    sourceAuthority: 'FUTA Registry service schedule',
  },
  {
    id: 'svc-futa-clearance',
    institutionId: 'futa',
    category: 'University Administrative Services',
    name: 'Departmental clearance',
    description: 'The sign-off chain a graduating student needs before records are released.',
    eligibility: 'Final-year students.',
    origin: 'official',
    modes: ['official_digital'],
    officialProvider: 'Your department and the Bursary',
    officialMethod: 'Clearance form signed in person at each office',
    fees: [{ label: 'Official fee', amount: 0, payee: 'institution', unknown: true }],
    processingEstimate: null,
    requirements: [
      { id: 'r1', label: 'Library clearance', source: 'institution' },
      { id: 'r2', label: 'Bursary clearance', source: 'institution' },
      { id: 'r3', label: 'Hostel clearance, if you lived on campus', source: 'institution', optional: true },
    ],
    requiresAuthorisation: false,
    collectionMethods: ['Signed form returned to you'],
    deliveryOptions: ['Collect in person'],
    restrictions: ['Each office signs in person. No part of this can be done through Campuzon.'],
    catalogueVerified: false,
    lastUpdated: '2 Aug 2026',
    sourceAuthority: null,
  },

  /* -------------------------------------------------------------- UNILAG ---- */
  {
    id: 'svc-unilag-transcript',
    institutionId: 'unilag',
    category: 'Transcripts & Records',
    name: 'Academic transcript',
    description:
      'An official transcript issued through the UNILAG transcript platform and sent to the body you name.',
    eligibility: 'Current students and graduates in good standing.',
    origin: 'integrated',
    modes: ['institutional_integration', 'official_digital'],
    officialProvider: 'University of Lagos Registry',
    officialMethod: 'UNILAG transcript platform, connected to Campuzon',
    fees: [
      { label: 'Official transcript fee (local)', amount: 12000, payee: 'institution' },
      { label: 'Campuzon processing fee', amount: 1000, payee: 'campuzon' },
    ],
    processingEstimate: '7 – 14 working days, with status updates in Campuzon',
    requirements: [
      { id: 'r1', label: 'Matriculation number', source: 'institution' },
      { id: 'r2', label: 'Recipient name and address', source: 'institution' },
      { id: 'r3', label: 'Consent to share your academic record with the Registry', source: 'campuzon' },
    ],
    requiresAuthorisation: false,
    collectionMethods: ['Sealed courier from the Registry'],
    deliveryOptions: ['Courier within Nigeria', 'International courier'],
    restrictions: ['Requests are fulfilled by the Registry. Campuzon only carries them.'],
    catalogueVerified: true,
    lastUpdated: '11 Aug 2026',
    sourceAuthority: 'UNILAG Registry integration agreement',
    popular: true,
  },
  {
    id: 'svc-unilag-reference',
    institutionId: 'unilag',
    category: 'References & Recommendations',
    name: 'Reference or recommendation letter',
    description:
      'Ask a lecturer or supervisor for a reference. They write and submit it themselves.',
    eligibility: 'Anyone with a referee willing to act.',
    origin: 'assisted',
    modes: ['assisted'],
    officialProvider: null,
    officialMethod: null,
    fees: [{ label: 'No fee', amount: 0, payee: 'campuzon' }],
    processingEstimate: 'Depends entirely on your referee',
    requirements: [
      { id: 'r1', label: 'Referee name, role and email address', source: 'campuzon' },
      { id: 'r2', label: 'Who the reference is for, and by when', source: 'campuzon' },
      { id: 'r3', label: 'Your CV', source: 'campuzon' },
    ],
    requiresAuthorisation: false,
    collectionMethods: ['Submitted by the referee'],
    deliveryOptions: ['Direct to recipient', 'Returned to you', 'Campuzon verified link'],
    restrictions: [
      'Campuzon never writes, edits or signs a reference.',
      'A confidential reference is not readable by you at any point.',
    ],
    catalogueVerified: true,
    lastUpdated: '11 Aug 2026',
    sourceAuthority: 'Campuzon service terms',
    popular: true,
  },
  {
    id: 'svc-unilag-english',
    institutionId: 'unilag',
    category: 'Letters & Certifications',
    name: 'English proficiency letter',
    description:
      'Confirms your programme was taught and examined in English, for universities that accept it in place of a test.',
    eligibility: 'Graduates and final-year students.',
    origin: 'official',
    modes: ['official_digital', 'assisted'],
    officialProvider: 'University of Lagos Registry',
    officialMethod: 'Application to the Registrar naming the receiving university',
    fees: [
      { label: 'Official fee', amount: 7500, payee: 'institution' },
      { label: 'Assistance fee', amount: 5000, payee: 'provider' },
    ],
    processingEstimate: '5 – 10 working days',
    requirements: [
      { id: 'r1', label: 'Name of the receiving university', source: 'institution' },
      { id: 'r2', label: 'Programme and years of study', source: 'institution' },
      { id: 'r3', label: 'Statement of result or transcript copy', source: 'institution' },
    ],
    requiresAuthorisation: false,
    collectionMethods: ['Registry counter', 'Authorised representative'],
    deliveryOptions: ['Collect in person', 'Email to the receiving university'],
    restrictions: ['Wording is at the Registrar’s discretion.'],
    catalogueVerified: true,
    lastUpdated: '11 Aug 2026',
    sourceAuthority: 'UNILAG Registry service schedule',
    popular: true,
  },
  {
    id: 'svc-unilag-collection',
    institutionId: 'unilag',
    category: 'Collection & Delivery',
    name: 'Document collection on campus',
    description:
      'A verified representative collects a released document and hands it to you or a courier.',
    eligibility: 'The document must already be marked ready.',
    origin: 'third_party',
    modes: ['assisted'],
    officialProvider: null,
    officialMethod: null,
    fees: [
      { label: 'Collection fee', amount: 4500, payee: 'provider' },
      { label: 'Courier, if needed', amount: 3000, payee: 'courier' },
    ],
    processingEstimate: '1 – 3 working days',
    requirements: [
      { id: 'r1', label: 'Signed authorisation naming the representative', source: 'campuzon' },
      { id: 'r2', label: 'Photo of your student ID', source: 'provider' },
    ],
    requiresAuthorisation: true,
    collectionMethods: ['Authorised representative'],
    deliveryOptions: ['Courier within Nigeria', 'Hand to you in Lagos'],
    restrictions: ['An independent service. The representative does not act for the university.'],
    catalogueVerified: true,
    lastUpdated: '11 Aug 2026',
    sourceAuthority: 'Campuzon provider terms',
    popular: true,
  },
  {
    id: 'svc-unilag-id',
    institutionId: 'unilag',
    category: 'University Administrative Services',
    name: 'Student ID card replacement',
    description: 'Replaces a lost or damaged student identity card.',
    eligibility: 'Registered students only.',
    origin: 'official',
    modes: ['official_digital'],
    officialProvider: 'Student Affairs Division',
    officialMethod: 'Police report, then application at Student Affairs',
    fees: [{ label: 'Official fee', amount: 3000, payee: 'institution' }],
    processingEstimate: '3 – 7 working days',
    requirements: [
      { id: 'r1', label: 'Police report for the lost card', source: 'institution' },
      { id: 'r2', label: 'Passport photograph', source: 'institution' },
    ],
    requiresAuthorisation: false,
    collectionMethods: ['Student Affairs counter'],
    deliveryOptions: ['Collect in person'],
    restrictions: ['Collected in person. Nobody may collect an ID card for you.'],
    catalogueVerified: true,
    lastUpdated: '11 Aug 2026',
    sourceAuthority: 'UNILAG Student Affairs',
  },
  {
    id: 'svc-unilag-records',
    institutionId: 'unilag',
    category: 'Document Assistance',
    name: 'Application document review',
    description:
      'A verified provider checks your postgraduate application pack against the receiving university’s stated requirements.',
    eligibility: 'Open to all Campuzon users.',
    origin: 'third_party',
    modes: ['assisted'],
    officialProvider: null,
    officialMethod: null,
    fees: [{ label: 'Review fee', amount: 12000, payee: 'provider' }],
    processingEstimate: '2 – 4 working days',
    requirements: [
      { id: 'r1', label: 'Your draft documents', source: 'provider' },
      { id: 'r2', label: 'Link to the programme requirements', source: 'provider' },
    ],
    requiresAuthorisation: false,
    collectionMethods: ['Returned in Campuzon'],
    deliveryOptions: ['Returned in Campuzon'],
    restrictions: [
      'Advisory only. A review is not an assessment of your chances and carries no institutional weight.',
    ],
    catalogueVerified: true,
    lastUpdated: '11 Aug 2026',
    sourceAuthority: 'Campuzon provider terms',
  },
]

export const serviceProviders: ServiceProvider[] = [
  {
    id: 'prv-1',
    name: 'Akure Campus Errands',
    status: 'Verified',
    institutionsServed: ['futa'],
    serviceIds: ['svc-futa-transcript', 'svc-futa-collection', 'svc-futa-verification'],
    location: 'Akure, Ondo State',
    feeFrom: 5000,
    processingEstimate: '1 – 3 working days on campus',
    terms: 'Payment held until you confirm the document reached you. Refund if the Registry refuses release.',
    deliveryOptions: ['Hand delivery in Akure', 'Courier within Nigeria'],
    completed: 214,
    disputes: 2,
    ratingLabel: '4.8 from 189 completed requests',
    verifiedOn: '14 Mar 2026',
    authorityNote:
      'Independent provider. Not an agent of FUTA and cannot speak for the Registry or influence a decision.',
  },
  {
    id: 'prv-2',
    name: 'Chidera N. — Registry Runner',
    status: 'Verified',
    institutionsServed: ['futa', 'unilag'],
    serviceIds: ['svc-futa-collection', 'svc-unilag-collection', 'svc-unilag-english'],
    location: 'Akure and Lagos',
    feeFrom: 4500,
    processingEstimate: '2 – 5 working days',
    terms: 'Photographic proof of collection provided. Courier arranged at cost.',
    deliveryOptions: ['Courier within Nigeria'],
    completed: 96,
    disputes: 0,
    ratingLabel: '4.9 from 88 completed requests',
    verifiedOn: '2 Jun 2026',
    authorityNote: 'Independent provider verified by Campuzon. Holds no institutional authority.',
  },
  {
    id: 'prv-3',
    name: 'Lagos Docs Support',
    status: 'Verified',
    institutionsServed: ['unilag'],
    serviceIds: ['svc-unilag-collection', 'svc-unilag-records'],
    location: 'Yaba, Lagos',
    feeFrom: 4500,
    processingEstimate: '1 – 4 working days',
    terms: 'Escrow released on your confirmation. Cancel free before collection begins.',
    deliveryOptions: ['Hand delivery in Lagos', 'Courier within Nigeria'],
    completed: 132,
    disputes: 1,
    ratingLabel: '4.7 from 121 completed requests',
    verifiedOn: '20 Apr 2026',
    authorityNote: 'Independent provider. Not affiliated with the University of Lagos.',
  },
  {
    id: 'prv-4',
    name: 'SwiftPath Academic Services',
    status: 'Pending verification',
    institutionsServed: ['futa'],
    serviceIds: ['svc-futa-transcript'],
    location: 'Akure, Ondo State',
    feeFrom: 6000,
    processingEstimate: 'Not yet published',
    terms: 'Not yet reviewed by Campuzon.',
    deliveryOptions: [],
    completed: 0,
    disputes: 0,
    ratingLabel: 'No completed requests yet',
    authorityNote:
      'Verification incomplete. Cannot be selected, and must not be presented as a Campuzon Verified Provider.',
  },
]

export const authorisations: Authorisation[] = [
  {
    id: 'auth-1',
    purpose: 'Collection of academic transcript',
    authorisedParty: 'Akure Campus Errands',
    institutionId: 'futa',
    scope: ['Collect a sealed transcript from Exams and Records'],
    validUntil: '30 Sep 2026',
    signedOn: '9 Aug 2026',
    acknowledgedOn: '9 Aug 2026',
    revokedOn: null,
    institutionAccepts: 'unconfirmed',
  },
]

export const serviceRequests: ServiceRequest[] = [
  {
    id: 'CR-2026-000184',
    serviceId: 'svc-futa-transcript',
    institutionId: 'futa',
    purpose: 'MSc application — University of Leeds',
    deadline: '30 Sep 2026',
    mode: 'assisted',
    providerId: 'prv-1',
    status: 'Processing',
    createdOn: '8 Aug 2026',
    fees: [
      { label: 'Official transcript fee', amount: 10000, payee: 'institution' },
      { label: 'Provider assistance fee', amount: 7500, payee: 'provider' },
      { label: 'Courier to Leeds', amount: 18500, payee: 'courier' },
    ],
    paymentState: 'paid',
    timeline: [
      { id: 't1', label: 'Request created', at: '8 Aug 2026, 10:12', actor: 'You', state: 'done' },
      {
        id: 't2',
        label: 'Provider selected',
        at: '8 Aug 2026, 10:40',
        actor: 'You',
        note: 'Akure Campus Errands',
        state: 'done',
      },
      { id: 't3', label: 'Provider accepted', at: '8 Aug 2026, 16:05', actor: 'Provider', state: 'done' },
      {
        id: 't4',
        label: 'Documents submitted',
        at: '9 Aug 2026, 09:20',
        actor: 'You',
        note: 'Authorisation signed and acknowledged',
        state: 'done',
      },
      {
        id: 't5',
        label: 'Application submitted to Registry',
        at: '11 Aug 2026, 11:00',
        actor: 'Provider',
        state: 'done',
      },
      {
        id: 't6',
        label: 'Processing at the Registry',
        at: '11 Aug 2026, 11:00',
        actor: 'FUTA Registry',
        note: 'Registry timelines are outside Campuzon’s control.',
        state: 'current',
      },
      { id: 't7', label: 'Ready for collection', at: null, actor: 'FUTA Registry', state: 'upcoming' },
      { id: 't8', label: 'Collected', at: null, actor: 'Provider', state: 'upcoming' },
      { id: 't9', label: 'In delivery', at: null, actor: 'Courier', state: 'upcoming' },
      { id: 't10', label: 'Delivered', at: null, actor: 'Courier', state: 'upcoming' },
      { id: 't11', label: 'Completed', at: null, actor: 'You', state: 'upcoming' },
    ],
    documents: [
      { id: 'd1', label: 'Matriculation details', state: 'uploaded', uploadedOn: '8 Aug 2026' },
      { id: 'd2', label: 'Official payment reference', state: 'verified', uploadedOn: '9 Aug 2026' },
      { id: 'd3', label: 'Signed authorisation', state: 'verified', uploadedOn: '9 Aug 2026' },
      {
        id: 'd4',
        label: 'Sealed transcript',
        state: 'required',
        detail: 'Issued by the Registry — not yet released.',
      },
    ],
    messages: [
      {
        id: 'm1',
        author: 'System',
        role: 'System',
        body: 'Request created for Academic transcript at FUTA.',
        at: '8 Aug 2026, 10:12',
      },
      {
        id: 'm2',
        author: 'Akure Campus Errands',
        role: 'Provider',
        body: 'Accepted. I will need your signed authorisation before the Registry will speak to me.',
        at: '8 Aug 2026, 16:05',
      },
      {
        id: 'm3',
        author: 'You',
        role: 'Student',
        body: 'Signed and attached. Deadline is 30 September for Leeds.',
        at: '9 Aug 2026, 09:20',
        attachment: 'authorisation-CR-2026-000184.pdf',
      },
      {
        id: 'm4',
        author: 'Akure Campus Errands',
        role: 'Provider',
        body: 'Submitted at Exams and Records this morning. They quoted two to three weeks.',
        at: '11 Aug 2026, 11:04',
      },
    ],
    authorisationId: 'auth-1',
    shareScope: [
      { key: 'name', label: 'Name and matriculation number', on: true },
      { key: 'programme', label: 'Programme and graduation year', on: true },
      { key: 'cv', label: 'CV', on: false },
      { key: 'projects', label: 'Project information', on: false },
      {
        key: 'notes',
        label: 'Private Notes',
        on: false,
        locked: true,
        lockedReason: 'Notes are never shared with a provider, referee or institution.',
      },
      {
        key: 'diary',
        label: 'Personal Diary',
        on: false,
        locked: true,
        lockedReason: 'Diary entries are visible only to you.',
      },
    ],
    applicationId: 'app-leeds-msc',
    noteIds: ['n-2'],
    collection: {
      method: 'Authorised representative',
      deliveryTo: 'Leeds, United Kingdom',
      tracking: 'Awaiting release before a courier is booked',
    },
  },
  {
    id: 'CR-2026-000201',
    serviceId: 'svc-unilag-english',
    institutionId: 'unilag',
    purpose: 'MSc application — University of Leeds',
    deadline: '15 Sep 2026',
    mode: 'official_digital',
    providerId: null,
    status: 'Awaiting institution',
    createdOn: '12 Aug 2026',
    fees: [{ label: 'Official fee', amount: 7500, payee: 'institution' }],
    paymentState: 'paid',
    timeline: [
      { id: 't1', label: 'Request created', at: '12 Aug 2026, 14:02', actor: 'You', state: 'done' },
      {
        id: 't2',
        label: 'Application lodged with the Registrar',
        at: '12 Aug 2026, 15:30',
        actor: 'You',
        note: 'Lodged on the official channel. Campuzon is tracking it, not processing it.',
        state: 'done',
      },
      { id: 't3', label: 'Awaiting the Registrar', at: '12 Aug 2026, 15:30', actor: 'UNILAG Registry', state: 'current' },
      { id: 't4', label: 'Ready for collection', at: null, actor: 'UNILAG Registry', state: 'upcoming' },
      { id: 't5', label: 'Completed', at: null, actor: 'You', state: 'upcoming' },
    ],
    documents: [
      { id: 'd1', label: 'Receiving university details', state: 'uploaded', uploadedOn: '12 Aug 2026' },
      { id: 'd2', label: 'Statement of result copy', state: 'uploaded', uploadedOn: '12 Aug 2026' },
      { id: 'd3', label: 'Signed letter from the Registrar', state: 'required' },
    ],
    messages: [
      {
        id: 'm1',
        author: 'System',
        role: 'System',
        body: 'Tracked as an official service. Campuzon holds no part of this process.',
        at: '12 Aug 2026, 14:02',
      },
    ],
    authorisationId: null,
    shareScope: [
      { key: 'name', label: 'Name and matriculation number', on: true },
      { key: 'programme', label: 'Programme and years of study', on: true },
      {
        key: 'notes',
        label: 'Private Notes',
        on: false,
        locked: true,
        lockedReason: 'Notes are never shared with an institution.',
      },
    ],
    applicationId: 'app-leeds-msc',
    noteIds: [],
  },
  {
    id: 'CR-2026-000167',
    serviceId: 'svc-futa-verification',
    institutionId: 'futa',
    purpose: 'Employment check — Dangote Group',
    deadline: null,
    mode: 'assisted',
    providerId: 'prv-1',
    status: 'Completed',
    createdOn: '2 Jul 2026',
    fees: [
      { label: 'Official verification fee', amount: 15000, payee: 'institution' },
      { label: 'Provider assistance fee', amount: 6000, payee: 'provider' },
    ],
    paymentState: 'paid',
    timeline: [
      { id: 't1', label: 'Request created', at: '2 Jul 2026', actor: 'You', state: 'done' },
      { id: 't2', label: 'Provider accepted', at: '2 Jul 2026', actor: 'Provider', state: 'done' },
      { id: 't3', label: 'Submitted to Registry', at: '4 Jul 2026', actor: 'Provider', state: 'done' },
      { id: 't4', label: 'Registry confirmed to employer', at: '18 Jul 2026', actor: 'FUTA Registry', state: 'done' },
      { id: 't5', label: 'Completed', at: '18 Jul 2026', actor: 'You', state: 'done' },
    ],
    documents: [
      { id: 'd1', label: 'Certificate scan', state: 'uploaded', uploadedOn: '2 Jul 2026' },
      { id: 'd2', label: 'Registry confirmation letter', state: 'issued', uploadedOn: '18 Jul 2026' },
    ],
    messages: [
      {
        id: 'm1',
        author: 'Akure Campus Errands',
        role: 'Provider',
        body: 'Registry wrote directly to the employer on 18 July. Nothing further needed from you.',
        at: '18 Jul 2026',
      },
    ],
    authorisationId: null,
    shareScope: [
      { key: 'name', label: 'Name and matriculation number', on: true },
      {
        key: 'notes',
        label: 'Private Notes',
        on: false,
        locked: true,
        lockedReason: 'Notes are never shared.',
      },
    ],
    noteIds: [],
    verification: {
      reference: 'CR-2026-000167',
      verifiedOn: '18 Jul 2026',
      submittedBy: 'FUTA Registry — Certificate Verification Unit',
      exposes: [
        'That a verification was completed',
        'The institution and service',
        'The date it was confirmed',
      ],
      withheld: [
        'The certificate itself',
        'Your grades and academic record',
        'Your contact details',
        'The employer’s correspondence',
      ],
    },
  },
]

export const referenceRequests: ReferenceRequest[] = [
  {
    id: 'REF-2026-0042',
    kind: 'Graduate school recommendation',
    refereeName: 'Prof. N. Okonkwo',
    refereeRole: 'Head of Department, Systems Engineering — UNILAG',
    refereeEmail: 'n.okonkwo@unilag.edu.ng',
    recipient: 'University of Leeds — MSc Energy Systems',
    confidential: true,
    submissionMethod: 'Direct to recipient',
    deadline: '20 Sep 2026',
    status: 'Accepted',
    requestedOn: '6 Aug 2026',
    respondedOn: '7 Aug 2026',
    submittedOn: null,
    attachments: ['CV — Amarachi Obi.pdf', 'Personal statement draft.pdf'],
    remindersSent: 1,
    applicationId: 'app-leeds-msc',
    notesForReferee:
      'Focus on the solar dryer project if possible — it is the closest work to the programme.',
  },
  {
    id: 'REF-2026-0051',
    kind: 'Academic reference',
    refereeName: 'Dr. A. Balogun',
    refereeRole: 'Senior Lecturer, Operations Research — UNILAG',
    refereeEmail: 'a.balogun@unilag.edu.ng',
    recipient: 'University of Leeds — MSc Energy Systems',
    confidential: false,
    submissionMethod: 'Campuzon verified link',
    deadline: '20 Sep 2026',
    status: 'Awaiting referee',
    requestedOn: '11 Aug 2026',
    respondedOn: null,
    submittedOn: null,
    attachments: ['CV — Amarachi Obi.pdf'],
    remindersSent: 0,
    applicationId: 'app-leeds-msc',
    notesForReferee: 'SYE 311 coursework and the group project on queueing models.',
  },
  {
    id: 'REF-2026-0033',
    kind: 'Internship recommendation',
    refereeName: 'Mrs. F. Adeyemi',
    refereeRole: 'Team Lead, Operations — Interswitch',
    refereeEmail: 'f.adeyemi@interswitch.com',
    recipient: 'Flutterwave — Operations internship',
    confidential: false,
    submissionMethod: 'Returned to student',
    deadline: '5 Aug 2026',
    status: 'Submitted',
    requestedOn: '18 Jul 2026',
    respondedOn: '19 Jul 2026',
    submittedOn: '30 Jul 2026',
    attachments: ['CV — Amarachi Obi.pdf'],
    remindersSent: 1,
    notesForReferee: 'Industrial training placement, June to August 2025.',
  },
]

/** What the student's Leeds application still needs, across services and documents. */
export const applicationReadiness = {
  id: 'app-leeds-msc',
  label: 'MSc Energy Systems — University of Leeds',
  deadline: '30 Sep 2026',
  items: [
    { label: 'CV', done: true, via: 'Career profile' },
    { label: 'Personal statement', done: true, via: 'Uploaded document' },
    { label: 'Transcript', done: false, via: 'Service request CR-2026-000184', requestId: 'CR-2026-000184' },
    { label: 'Certificate', done: true, via: 'Uploaded document' },
    { label: 'Recommendation 1', done: false, via: 'REF-2026-0042 — accepted, not yet submitted', referenceId: 'REF-2026-0042' },
    { label: 'Recommendation 2', done: false, via: 'REF-2026-0051 — awaiting referee', referenceId: 'REF-2026-0051' },
    {
      label: 'English proficiency letter',
      done: false,
      via: 'Service request CR-2026-000201',
      requestId: 'CR-2026-000201',
    },
  ],
}

/** Provider-side queue for the service provider console. */
export const providerServiceQueue = [
  {
    requestId: 'CR-2026-000184',
    student: 'Amarachi O.',
    service: 'Academic transcript — FUTA',
    status: 'Processing' as const,
    deadline: '30 Sep 2026',
    fee: 7500,
    pendingAction: 'Update status when the Registry releases the document',
    visibleFields: ['Name and matriculation number', 'Programme and graduation year'],
    withheldFields: ['CV', 'Project information', 'Notes', 'Diary', 'Financial records'],
  },
  {
    requestId: 'CR-2026-000212',
    student: 'Tunde B.',
    service: 'Document collection — FUTA',
    status: 'Awaiting provider' as const,
    deadline: '22 Aug 2026',
    fee: 5000,
    pendingAction: 'Accept or decline',
    visibleFields: ['Name and matriculation number'],
    withheldFields: ['CV', 'Academic record', 'Notes', 'Diary', 'Financial records'],
  },
  {
    requestId: 'CR-2026-000208',
    student: 'Ifeoma E.',
    service: 'Certificate verification — FUTA',
    status: 'Awaiting information' as const,
    deadline: '29 Aug 2026',
    fee: 6000,
    pendingAction: 'Certificate scan is unreadable — request a clearer copy',
    visibleFields: ['Name and matriculation number'],
    withheldFields: ['CV', 'Academic record', 'Notes', 'Diary', 'Financial records'],
  },
]

/** Institution-side view. Aggregate and request-level only, never Notes or wellbeing. */
export const institutionServiceLoad = {
  officiallyProvided: ['Academic transcript', 'English proficiency letter', 'Student ID replacement'],
  notOfferedThroughCampuzon: ['Departmental clearance', 'Certificate reissue'],
  queues: [
    { label: 'Transcript requests', open: 148, overdue: 12, medianDays: 11 },
    { label: 'Verification requests', open: 62, overdue: 4, medianDays: 8 },
    { label: 'Letters and certifications', open: 39, overdue: 9, medianDays: 13 },
    { label: 'Reference requests carried to staff', open: 87, overdue: 0, medianDays: 6 },
  ],
  providers: [
    { name: 'Chidera N. — Registry Runner', status: 'Verified', requests: 34 },
    { name: 'Lagos Docs Support', status: 'Verified', requests: 71 },
  ],
}

/** Campuzon admin control centre. */
export const servicesAdmin = {
  metrics: [
    { label: 'Open requests', value: '1,284', note: 'across 2 institutions' },
    { label: 'Providers verified', value: '38', note: '4 pending, 1 suspended' },
    { label: 'Median completion', value: '12 days', note: 'assisted requests' },
    { label: 'Open disputes', value: '7', note: '3 past the response window' },
  ],
  verificationQueue: [
    {
      id: 'PV-118',
      name: 'SwiftPath Academic Services',
      submitted: '10 Aug 2026',
      missing: ['Government ID not yet matched', 'No institutional reference supplied'],
    },
    {
      id: 'PV-119',
      name: 'Ondo Campus Logistics',
      submitted: '12 Aug 2026',
      missing: ['Awaiting address confirmation'],
    },
  ],
  disputes: [
    {
      id: 'DS-2026-041',
      requestId: 'CR-2026-000149',
      raisedBy: 'Student',
      reason: 'Provider charged above the published fee',
      state: 'Provider responded — under review',
      escrow: 12500,
    },
    {
      id: 'DS-2026-044',
      requestId: 'CR-2026-000173',
      raisedBy: 'Student',
      reason: 'Document never collected before the deadline',
      state: 'Refund approved, awaiting payout reversal',
      escrow: 8500,
    },
  ],
  integrityFlags: [
    {
      id: 'IF-77',
      severity: 'High',
      detail: 'Provider listing used the FUTA crest and the phrase “official FUTA agent”.',
      action: 'Listing removed, provider restricted pending review.',
    },
    {
      id: 'IF-81',
      severity: 'High',
      detail: 'Provider asked a student for their portal password in a request thread.',
      action: 'Account suspended. Credential sharing is prohibited.',
    },
    {
      id: 'IF-84',
      severity: 'Medium',
      detail: 'Duplicate transcript requests raised by one account across three providers.',
      action: 'Flagged for review. No action against the student yet.',
    },
  ],
  auditLog: [
    {
      at: '14 Aug 2026, 09:12',
      actor: 'K. Adeniyi (Compliance)',
      action: 'Suspended provider PRV-221 after credential-sharing report',
    },
    {
      at: '13 Aug 2026, 16:40',
      actor: 'S. Umeh (Services admin)',
      action: 'Updated FUTA transcript catalogue entry — processing estimate',
    },
    {
      at: '13 Aug 2026, 11:05',
      actor: 'S. Umeh (Services admin)',
      action: 'Marked English proficiency letter fee as unavailable pending Registry confirmation',
    },
    {
      at: '12 Aug 2026, 14:22',
      actor: 'K. Adeniyi (Compliance)',
      action: 'Approved refund on DS-2026-044',
    },
  ],
}
